import {
  Context,
  Liquid,
  type PropertyAccessToken,
  type Template,
  type Token,
  Tokenizer,
  type TopLevelToken,
  type Value,
  TypeGuards,
} from "@hightouch/lib/templating/liquid";

/**
 * Canonical Liquid variable extractor for CEP templates, shared by backend
 * strict validation (`render-template.ts`) and the frontend editor save
 * (`extract-profile-mappings.ts`) so both recognize the same syntax: `{{ simple }}`,
 * `{{ row.field }}`, `{{ row['field'] }}`/`["field"]`, and top-level `{{ ["field"] }}`.
 *
 * Optional Liquid filters are tolerated; only `default` affects extraction (it
 * suppresses missing/null-value errors during strict validation).
 */

/** Variable-name syntax, shared by every regex here. */
const VAR_NAME_PATTERN = String.raw`(?:row\.(?<dotField>[a-zA-Z_][a-zA-Z0-9_]*)|row\['(?<bracketField>[^'"]+)'\]|row\["(?<quoteField>[^'"]+)"\]|\["(?<topDouble>[^"]+)"\]|\['(?<topSingle>[^']+)'\]|(?<simpleField>[a-zA-Z_][a-zA-Z0-9_]*))`;

/**
 * Regex shared by `extractTemplateVariables` and the sourcemap instrumenter so
 * validated variables and sourcemap ranges stay in lock-step. Exported for the latter.
 */
const FILTERS_PATTERN = String.raw`(?<filters>\s*\|[^}]*)?`;

export const LIQUID_VAR_REGEX = new RegExp(
  String.raw`\{\{\s*${VAR_NAME_PATTERN}${FILTERS_PATTERN}\s*\}\}`,
  "g",
);

/** Extract the variable name from a `LIQUID_VAR_REGEX` match. */
export function variableNameFromMatch(
  match: RegExpMatchArray,
): string | undefined {
  return (
    match.groups?.dotField ??
    match.groups?.bracketField ??
    match.groups?.quoteField ??
    match.groups?.topDouble ??
    match.groups?.topSingle ??
    match.groups?.simpleField
  );
}

const LIQUID = new Liquid();

const HAS_DEFAULT_RE = /\bdefault\s*:/;

/** First argument of a `default:` filter. Stops at a comma
 *  (`default: "x", allow_false: true`) or the next filter in a chain. */
const DEFAULT_ARG_RE =
  /\|\s*default\s*:\s*(?:("(?:[^"\\]|\\.)*")|('(?:[^'\\]|\\.)*')|(\{\{[^}]*)|([^,|}\s]+))/;

/** The merge-tag picker inserts a variable as a nested output tag
 *  (`default: {{ ['nickname'] }}`). The filters capture ends at its first `}`,
 *  so match the opening tag that survives. */
const NESTED_DEFAULT_ARG_RE = new RegExp(
  String.raw`^\{\{\s*${VAR_NAME_PATTERN}`,
);

const NUMERIC_LITERAL_RE = /^-?\d+(?:\.\d+)?$/;

/** `literal` is text the recipient sees (possibly empty); `expression` is an
 *  unquoted argument we can name but not evaluate. */
export type TemplateVariableDefault =
  | { kind: "literal"; value: string }
  | { kind: "expression"; source: string };

function liquidLiteralText(quoted: string): string | undefined {
  try {
    const value = LIQUID.evalValueSync(quoted, new Context({}));
    return typeof value === "string" ? value : undefined;
  } catch {
    return undefined;
  }
}

/** The `default:` argument in a filters string, or undefined when there is none.
 *  Assumes entities are already decoded — `normalizeLiquidTagEntities` owns that,
 *  and decoding again here would skip its raw-quote guard. */
export function parseDefaultArgument(
  filters: string,
): TemplateVariableDefault | undefined {
  const m = DEFAULT_ARG_RE.exec(filters);
  if (!m) return undefined;
  const [, doubleQuoted, singleQuoted, nested, bare] = m;
  const quoted = doubleQuoted ?? singleQuoted;
  if (quoted !== undefined) {
    const value = liquidLiteralText(quoted);
    return value === undefined ? undefined : { kind: "literal", value };
  }
  if (nested !== undefined) {
    const inner = NESTED_DEFAULT_ARG_RE.exec(nested);
    const name = inner ? variableNameFromMatch(inner) : undefined;
    // No name worth showing — better than a fragment of the tag's syntax.
    return name ? { kind: "expression", source: name } : undefined;
  }
  if (bare === undefined) return undefined;
  // Numbers and booleans render as themselves; anything else is a reference.
  if (NUMERIC_LITERAL_RE.test(bare) || bare === "true" || bare === "false") {
    return { kind: "literal", value: bare };
  }
  return { kind: "expression", source: bare };
}

/** Identity of a default argument, for de-duplicating repeated usages. */
export function defaultValueKey(value: TemplateVariableDefault): string {
  return value.kind === "literal"
    ? `literal:${value.value}`
    : `expression:${value.source}`;
}

export type TemplateVariable = {
  name: string;
  hasDefault: boolean;
  conflict: boolean;
  defaultValues: TemplateVariableDefault[];
};

/**
 * Unique variable references in a Liquid template. A variable appearing with
 * inconsistent default-filter usage is marked `conflict: true`.
 */
export function extractTemplateVariables(template: string): TemplateVariable[] {
  const seen = new Map<string, boolean>();
  const defaults = new Map<string, Map<string, TemplateVariableDefault>>();
  const conflicts = new Set<string>();
  for (const m of template.matchAll(LIQUID_VAR_REGEX)) {
    const name = variableNameFromMatch(m);
    if (!name) continue;
    const filters = m.groups?.filters ?? "";
    const hasDefault = HAS_DEFAULT_RE.test(filters);
    if (!seen.has(name)) {
      seen.set(name, hasDefault);
    } else if (seen.get(name) !== hasDefault) {
      conflicts.add(name);
    }
    const parsed = parseDefaultArgument(filters);
    if (parsed) {
      let byKey = defaults.get(name);
      if (!byKey) {
        byKey = new Map();
        defaults.set(name, byKey);
      }
      const key = defaultValueKey(parsed);
      if (!byKey.has(key)) byKey.set(key, parsed);
    }
  }
  return [...seen.entries()].map(([name, hasDefault]) => ({
    name,
    hasDefault,
    conflict: conflicts.has(name),
    defaultValues: [...(defaults.get(name)?.values() ?? [])],
  }));
}

/** Unique variable names in a Liquid template — names-only wrapper over `extractTemplateVariables`. */
export function extractTemplateKeys(template: string): string[] {
  return extractTemplateVariables(template).map((v) => v.name);
}

// Names *bound within* a template by Liquid tags — `{% assign x = … %}`,
// `{% capture x %}…{% endcapture %}`, and `{% for x in … %}` / `{% tablerow x in … %}`
// loop iterators. These resolve at render time from the template itself, not from
// profile data, so a `{{ x }}` referencing one is NOT a missing column. Strict
// validation subtracts these before flagging `missing_column`, keeping the
// pre-send check in sync with the real render engine (which runs with
// `strictVariables: false` and evaluates the tags normally).
//
// Regex over the (stable) tag-open syntax, mirroring `LIQUID_VAR_REGEX`. The
// identifier charset matches Liquid's; `-?` tolerates whitespace-control markers.
// Over-matching only ever *suppresses* a would-be error (the safe direction);
// it can never introduce one.
const LIQUID_ASSIGN_RE = /\{%-?\s*assign\s+([a-zA-Z_][a-zA-Z0-9_]*)\s*=/g;
const LIQUID_CAPTURE_RE = /\{%-?\s*capture\s+([a-zA-Z_][a-zA-Z0-9_]*)\s*-?%\}/g;
const LIQUID_LOOP_RE =
  /\{%-?\s*(?:for|tablerow)\s+([a-zA-Z_][a-zA-Z0-9_]*)\s+in\s/g;

/** Variable names bound by `{% assign %}`, `{% capture %}`, or a `{% for/tablerow … in … %}` iterator. */
export function extractLocalBindingNames(template: string): Set<string> {
  const names = new Set<string>();
  for (const regex of [LIQUID_ASSIGN_RE, LIQUID_CAPTURE_RE, LIQUID_LOOP_RE]) {
    for (const match of template.matchAll(regex)) {
      if (match[1]) names.add(match[1]);
    }
  }
  return names;
}

/** Names bound in any of `templates`, for texts that render in one shared Liquid scope (assigns persist for the rest of the render). */
export function extractSharedScopeBindingNames(
  templates: Iterable<string>,
): Set<string> {
  const names = new Set<string>();
  for (const template of templates) {
    for (const name of extractLocalBindingNames(template)) names.add(name);
  }
  return names;
}

// Matches a single Liquid delimiter span — a tag (`{% … %}`) or an output
// (`{{ … }}`), including whitespace-control variants (`{%- … -%}`). Non-greedy
// so adjacent spans on one line stay distinct.
const LIQUID_DELIMITER_SPAN_RE = /\{%[\s\S]*?%\}|\{\{[\s\S]*?\}\}/g;

// HTML entity forms of `>` (gt) and `<` (lt): named (case-insensitive) plus
// decimal / hex numeric references, each tolerating leading zeros.
const GT_ENTITY_RE = /&(?:gt|#0*62|#x0*3e);/gi;
const LT_ENTITY_RE = /&(?:lt|#0*60|#x0*3c);/gi;

// Unlike `>`/`<`, decoding a quote inside a literal (`default: "say &quot;hi&quot;"`)
// would terminate it early, so `RAW_QUOTE_RE` guards the decode below.
const QUOT_ENTITY_RE = /&(?:quot|#0*34|#x0*22);/gi;
const APOS_ENTITY_RE = /&(?:apos|#0*39|#x0*27);/gi;
const RAW_QUOTE_RE = /["']/;

// Non-breaking-space entity inside a delimiter span (named + numeric forms).
// Deliberately does NOT match the raw U+00A0 char: liquidjs tokenizes the raw
// char fine (it parses and evaluates correctly in syntax positions and inside
// literals), so rewriting it would only risk changing a quoted literal that
// legitimately contains one. Only the *entity text* is unparseable syntax.
const NBSP_ENTITY_RE = /&(?:nbsp|#0*160|#x0*a0);/gi;

/**
 * Recover Liquid syntax that an HTML round-trip mangled inside `{% %}` /
 * `{{ }}` delimiters. Any HTML round-trip of a template — a DOM
 * parse/serialize, a rich-text editor (Beefree), a sanitizer — rewrites:
 *
 * - comparison operators: `{% if age > 18 %}` → `{% if age &gt; 18 %}`
 * - whitespace around chips / consecutive spaces: `{% if x  == "y" %}` →
 *   `{% if x&nbsp; == "y" %}` (contenteditable preserves spacing as `&nbsp;`)
 * - string literals: `default: "there"` → `default: &quot;there&quot;`, which
 *   liquidjs reads as a variable reference, so the filter yields nothing
 *
 * liquidjs can parse neither (an entity is not whitespace or an operator to
 * its tokenizer), surfacing as "invalid Liquid" in preview and failing the
 * same way at send time. So we rewrite entity `>`/`<` back to their chars and
 * `&nbsp;` to a regular space, *only* inside the delimiters —
 * legitimately-escaped `&gt;`/`&nbsp;`/`&amp;` elsewhere in the HTML body is
 * left untouched. Rewriting inside string literals or `{% raw %}` blocks is
 * safe too: `>`/`&gt;` and `&nbsp;`/space render equivalently in an HTML
 * email, so the output is visually identical for the browser.
 *
 * Caller contract: apply this to HTML-capable templates only. The equivalence
 * above holds for HTML; a plain-text channel (SMS/push/subject) renders the
 * string verbatim, where `&gt;` and `>` differ — so we must not guess there.
 * (Extraction-only callers are exempt — nothing they produce is rendered.)
 *
 * Lives here (not `render-template.ts`, which imports node:crypto) so
 * browser-side extraction callers can use it; re-exported from
 * `render-template` for backend render callers.
 */
export function normalizeLiquidTagEntities(template: string): string {
  // Fast path: nothing to rewrite without an entity. Skips the scan for the
  // overwhelming majority of templates.
  if (!template.includes("&")) return template;
  return template.replace(LIQUID_DELIMITER_SPAN_RE, (span) => {
    const decoded = span
      .replace(GT_ENTITY_RE, ">")
      .replace(LT_ENTITY_RE, "<")
      .replace(NBSP_ENTITY_RE, " ");
    // A span mixing raw and entity quotes is ambiguous: the entity is likely
    // escaped text inside a literal, where decoding would truncate it.
    return RAW_QUOTE_RE.test(decoded)
      ? decoded
      : decoded.replace(QUOT_ENTITY_RE, '"').replace(APOS_ENTITY_RE, "'");
  });
}

// A `{% … %}` tag span: opening delimiter through the first `%}`. Non-greedy
// so adjacent tags on one line stay distinct. The `\w` requirement makes the
// opener tag-shaped (`{% if`, `{%- assign`, `{% ht_…`), rejecting a FALSE span
// opened by a literal `{%` in body text (e.g. inside a `{{ "{%" }}` output
// literal) — such a span would run to some later tag's `%}` and swallow valid
// `{{ }}` outputs in between. The parse gate in `normalizeNestedOutputTags`
// backstops the tag-shaped false spans this regex can't distinguish.
const LIQUID_TAG_ONLY_SPAN_RE = /\{%-?\s*\w[\s\S]*?%\}/g;

// Within a tag span: a quoted string literal, OR a nested `{{ … }}` output tag
// (inner expression in group 1). One alternation so a `{{ }}` inside a quoted
// literal is consumed by the quote match and never unwrapped — Liquid string
// literals have no escape sequences, so the simple non-greedy quote scan is
// exact. A `'`/`"` inside a chip's bracket key (`{{ ['name'] }}`) is still
// unwrapped: the chip's `{{` sits before the quote, so the output-tag
// alternative wins at the earlier offset.
const QUOTED_OR_NESTED_OUTPUT_TAG_RE =
  /'[^']*'|"[^"]*"|\{\{\s*([\s\S]*?)\s*\}\}/g;

/**
 * A `{% raw %}…{% endraw %}` block. Liquid emits raw content verbatim, so a
 * normalizer that rewrites bytes visibly (unlike the render-equivalent entity
 * fixup above) must skip these ranges. Shared with the sourcemap
 * instrumentation in `render-template.ts`.
 */
export const RAW_BLOCK_RE =
  /\{%-?\s*raw\s*-?%\}[\s\S]*?\{%-?\s*endraw\s*-?%\}/g;

/** Start/end offsets (end exclusive) of every `{% raw %}` block in `template`. */
export function findRawBlockRanges(template: string): Array<[number, number]> {
  const ranges: Array<[number, number]> = [];
  for (const m of template.matchAll(RAW_BLOCK_RE)) {
    ranges.push([m.index, m.index + m[0].length]);
  }
  return ranges;
}

/**
 * Unwrap a `{{ … }}` output tag nested inside a `{% … %}` tag to its bare
 * expression: `{% if {{ ['name'] }} == "Alex" %}` → `{% if ['name'] == "Alex" %}`.
 * This is what the Beefree canvas emits when a merge-tag chip lands in a
 * hand-typed condition — invalid Liquid in every channel, while the bare form is
 * valid, so unwrapping recovers the marketer's intent instead of failing
 * preview and send.
 *
 * Skipped inside `{% raw %}` blocks and quoted literals
 * (`{% if x == "a {{ b }} c" %}`), which Liquid renders verbatim today —
 * unwrapping there would change sent content for templates that were never broken.
 *
 * A chip carrying a filter (`{{ ["gender"] | default: "F" }}`) is deliberately
 * left nested: conditions can't express a filtered value, and a keyed filter
 * parses with the comparison swallowed into its arguments (`default: "F" == "F"`
 * reads `"F" == "F"` as args), making the condition always-true — every
 * recipient would silently take one branch. Staying broken fails loudly instead;
 * the marketer's fix is to remove the chip's transform.
 *
 * A template that already parses is returned byte-for-byte. This rewrite deletes
 * delimiters, so it must only touch templates that are actually broken — the
 * gate is what protects a valid template whose body contains a literal `{%`
 * (a false tag span running to a later `%}` could otherwise swallow a real
 * `{{ }}`).
 */
export function normalizeNestedOutputTags(template: string): string {
  // Fast path: no output tag means nothing can be nested.
  if (!template.includes("{{")) return template;
  const rawRanges = findRawBlockRanges(template);
  const repaired = template.replace(
    LIQUID_TAG_ONLY_SPAN_RE,
    (span, offset: number) => {
      const end = offset + span.length;
      if (rawRanges.some(([rs, re]) => offset >= rs && end <= re)) return span;
      return span.replace(
        QUOTED_OR_NESTED_OUTPUT_TAG_RE,
        (full, inner: string | undefined) =>
          inner === undefined || hasTopLevelPipe(inner) ? full : inner,
      );
    },
  );
  if (repaired === template) return template;
  // Parse gate — only pays a parse when the scan wants to rewrite something,
  // which never happens on a healthy template. CEP custom tags are stripped
  // for the CHECK only (the bare engine doesn't know them and would reject
  // every real email); the returned value is never the stripped text.
  try {
    LIQUID.parse(stripCepCustomTags(template));
    return template;
  } catch {
    return repaired;
  }
}

/**
 * Whether a chip's inner expression carries a filter pipe. Pipes inside quoted
 * strings (bracket keys like `["a|b"]`, filter args like `join: "|"`) are not
 * delimiters, so the scan skips string literals.
 */
function hasTopLevelPipe(expression: string): boolean {
  let i = 0;
  while (i < expression.length) {
    const char = expression[i];
    if (char === '"' || char === "'") {
      const quote = char;
      i += 1;
      while (i < expression.length && expression[i] !== quote) i += 1;
      i += 1;
      continue;
    }
    if (char === "|") return true;
    i += 1;
  }
  return false;
}

// CEP custom tags (`{% ht_content_block id:"…" %}`, `{% ht_brand_footer %}`,
// and any future `ht_*` tag). The render pipeline registers/rewrites these
// before parsing, but the bare extraction engine below does not know them — a
// vanilla `Liquid.parse` throws "tag not found" on the FIRST one, which would
// silently kill control-flow extraction for the whole template. They carry no
// condition variables, so stripping is lossless for extraction.
// `end` is optional because a block tag's closing form (`{% endht_loc %}`) is the
// one spelling that does not start with `ht_`.
const CEP_CUSTOM_TAG_RE = /\{%-?\s*(?:end)?ht_\w+\b[\s\S]*?%\}/gi;

/**
 * All extraction-only repairs in one pass: entity fixup, nested-output-tag
 * unwrap, and custom-tag strip. For variable extraction/classification only —
 * never feed the result to a renderer (the custom tags are load-bearing there;
 * the render path applies the first two repairs itself and resolves the tags).
 *
 * `decodeEntities` mirrors the render path, which only decodes for HTML-capable
 * templates. Pass false for plain-text ones so what's extracted — a `default:`
 * literal especially — is what will actually render.
 */
export function repairLiquidForExtraction(
  template: string,
  { decodeEntities = true }: { decodeEntities?: boolean } = {},
): string {
  return stripCepCustomTags(
    normalizeNestedOutputTags(
      decodeEntities ? normalizeLiquidTagEntities(template) : template,
    ),
  );
}

export function extractAllTemplateKeys(template: string): string[] {
  const repaired = repairLiquidForExtraction(template);
  return [
    ...extractTemplateKeys(repaired),
    ...extractControlFlowVariables(repaired),
  ];
}

/**
 * Remove CEP custom tags from a template. For variable *extraction* only —
 * never feed the result to a renderer (the tags are load-bearing there).
 * All production callers go through `repairLiquidForExtraction`; exported
 * for direct testing.
 */
export function stripCepCustomTags(template: string): string {
  return template.replace(CEP_CUSTOM_TAG_RE, "");
}

// Extracts variables from CONDITIONS of {% if/elsif/unless/case/when/for %} tags
// — complements LIQUID_VAR_REGEX above, which finds {{ }} interpolation refs.
// `forloop` / `tablerowloop` (and their dot-access) are excluded as Liquid
// runtime-injected loop vars. `row.X` / `row["X"]` / `row['X']` normalize to bare
// X — matching LIQUID_VAR_REGEX's convention for {{ }} interpolation.
const EXCLUDED_ROOTS = new Set(["forloop", "tablerowloop"]);

/**
 * First source offset of every control-flow variable, in document order.
 * Parses `template` as given so offsets line up with a caller's own `{{ }}`
 * scan; pass `repairLiquidForExtraction(template)` to get the repaired forms.
 * LiquidJS 10 tokenizes tag arguments in place, so every collected token's
 * `begin` is already an absolute document offset.
 */
export function extractControlFlowVariablePositions(
  template: string,
): Map<string, number> {
  const positions = new Map<string, number>();
  let parsed: Template[];
  try {
    parsed = LIQUID.parse(template);
  } catch {
    // Malformed mid-edit input → no chips rather than a thrown error.
    return positions;
  }
  const collectConditionVars = (tag: TagNode): void => {
    if (tag.name === "if" || tag.name === "unless") {
      // Both tags keep EVERY arm's condition in `branches` — `{% if %}` plus its
      // `{% elsif %}`s, and `{% unless %}`'s primary condition as its sole branch.
      for (const b of tag.branches ?? []) {
        if (b.value) collectExpr(b.value, positions);
      }
    } else if (tag.name === "case") {
      // The `{% case subject %}` expression is `value`; each `{% when a, b %}`
      // arm carries its comparison operands in `values`.
      if (tag.value) collectExpr(tag.value, positions);
      for (const b of tag.branches ?? []) {
        for (const v of b.values ?? []) collectToken(v, positions);
      }
    } else if (tag.name === "for") {
      if (tag.collection) collectToken(tag.collection, positions);
      // limit:foo / offset:bar params can reference variables.
      for (const tok of Object.values(tag.hash?.hash ?? {})) {
        if (tok) collectToken(tok, positions);
      }
    }
  };
  for (const node of parsed) visitTagNodes(node, collectConditionVars);
  return new Map([...positions.entries()].sort(([, a], [, b]) => a - b));
}

export function extractControlFlowVariables(template: string): string[] {
  // Repair first: an entity-mangled tag, a nested `{{ }}` chip or a CEP custom
  // tag fails the whole-template parse and silently drops every condition
  // variable. Safe here because nothing this function touches is rendered.
  const positions = extractControlFlowVariablePositions(
    repairLiquidForExtraction(template),
  );
  return [...positions.keys()];
}

/**
 * The parsed-tag surface this module navigates. LiquidJS publishes no per-tag
 * shape, and a parsed tag IS the tag class's own instance — the fields its
 * constructor assigned sit directly on the node, so every property here is
 * optional and only the ones matching a given `name` are read.
 *
 * `value` is deliberately read ONLY for `{% case %}`: other tags (`{% assign %}`,
 * `{% echo %}`) also expose a `value`, and collecting from those would report
 * assignment right-hand sides as condition variables.
 */
type TagNode = {
  name: string;
  /** `{% for x in <collection> %}` */
  collection?: Token;
  /** `{% for … limit:foo offset:bar %}` params. */
  hash?: { hash: Record<string, Token | undefined> };
  /** `{% case <value> %}` subject. */
  value?: Value;
  /**
   * Arms of `{% if %}`/`{% unless %}` (condition in `value`) and of `{% case %}`
   * (a `{% when %}`'s operands in `values`, one entry per arm however many
   * values it lists).
   */
  branches?: { value?: Value; values?: Token[]; templates?: Template[] }[];
  /** Primary body of a non-branching block (`{% for %}`, `{% capture %}`, …). */
  templates?: Template[];
  elseTemplates?: Template[];
};

function isTagNode(node: unknown): node is TagNode {
  // A parsed tag is the only node kind carrying a `name`; `Output` (`{{ }}`) and
  // `HTML` (body text) have none.
  return (
    typeof node === "object" &&
    node !== null &&
    typeof (node as { name?: unknown }).name === "string"
  );
}

function record(
  positions: Map<string, number>,
  name: string,
  index: number,
): void {
  if (!positions.has(name)) positions.set(name, index);
}

// A conditional nested inside a non-conditional tag (e.g. `{% for %}`) must
// still be visited, hence the unconditional recursion.
function visitTagNodes(node: unknown, visit: (tag: TagNode) => void): void {
  if (!isTagNode(node)) return;
  visit(node);
  for (const child of node.templates ?? []) visitTagNodes(child, visit);
  for (const child of node.elseTemplates ?? []) visitTagNodes(child, visit);
  for (const b of node.branches ?? []) {
    for (const child of b.templates ?? []) visitTagNodes(child, visit);
  }
}

function collectExpr(value: Value, positions: Map<string, number>): void {
  for (const tok of value.initial.postfix ?? []) collectToken(tok, positions);
}

function collectToken(tok: Token, positions: Map<string, number>): void {
  if (TypeGuards.isPropertyAccessToken(tok)) {
    collectPropertyAccess(tok, positions);
  } else if (TypeGuards.isRangeToken(tok)) {
    collectToken(tok.lhs, positions);
    collectToken(tok.rhs, positions);
  }
}

export type ConditionalBranchSpan = { start: number; end: number };

const CONDITIONAL_TAGS = new Set(["if", "unless", "case"]);

/**
 * Source spans (offsets into `template`) of every conditionally rendered
 * branch body — `{% if %}`/`{% elsif %}`/`{% else %}`, `{% unless %}`, and
 * `{% case %}`/`{% when %}` arms — for the block-range sourcemap
 * instrumentation in `@hightouch/lib/cep/render-template`. Takes an already
 * parsed AST so callers can parse with an engine that has their custom tags
 * registered. Spans nest for nested conditionals. Bodies whose tokens index a
 * different source string (e.g. statements inside `{% liquid %}`) are skipped.
 */
export function collectConditionalBranchSpans(
  nodes: Template[],
  template: string,
): ConditionalBranchSpan[] {
  const spans: ConditionalBranchSpan[] = [];
  const seenBodies = new Set<Template[]>();
  // Tokenizing is delimiter-level and engine-agnostic (the operator set only
  // affects expression reading), so the module-level engine's options work
  // even for templates parsed with custom tags on the caller's engine.
  let topLevelTokens: TopLevelToken[] | null | undefined;
  const getTopLevelTokens = (): TopLevelToken[] | null => {
    if (topLevelTokens === undefined) {
      try {
        topLevelTokens = new Tokenizer(
          template,
          LIQUID.options.operators,
        ).readTopLevelTokens(LIQUID.options);
      } catch {
        // Unreachable after a successful parse; skip bookending rather than guess.
        topLevelTokens = null;
      }
    }
    return topLevelTokens;
  };
  const collectBodySpans = (tag: TagNode): void => {
    if (!CONDITIONAL_TAGS.has(tag.name)) return;
    // `if`, `unless` and `case` all put every arm's body in `branches`; the
    // `{% else %}` arm lands on `elseTemplates`. `templates` is read for
    // whichever of these tags keeps a primary body there. The seen-set guards
    // against the same array being reachable twice.
    const bodies = [
      tag.templates,
      ...(tag.branches ?? []).map((b) => b.templates),
      tag.elseTemplates,
    ];
    for (const body of bodies) {
      if (!body || body.length === 0 || seenBodies.has(body)) continue;
      seenBodies.add(body);
      const span = spanOfTemplates(body, template, getTopLevelTokens);
      if (span) spans.push(span);
    }
  };
  for (const node of nodes) visitTagNodes(node, collectBodySpans);
  return spans;
}

function spanOfTemplates(
  body: Template[],
  template: string,
  getTopLevelTokens: () => TopLevelToken[] | null,
): ConditionalBranchSpan | null {
  const lastNode = body[body.length - 1];
  const first = body[0]?.token;
  const last = lastNode?.token;
  if (!first || !last) return null;
  if (first.input !== template || last.input !== template) return null;
  let end = last.end;
  // A block tag's token covers only its opening delimiter, so `last.end` would
  // land the close marker inside the nested block's first branch (whose output
  // is dropped when that branch isn't taken — an unclosed marker). Extend the
  // span past the matching end tag. Standalone tags (`{% assign %}`, custom
  // tags) have no closer and keep `last.end`, which covers the whole tag.
  if (isTagNode(lastNode)) {
    const tokens = getTopLevelTokens();
    if (tokens === null) return null;
    let endTagEnd: number | null;
    try {
      endTagEnd = findMatchingEndTagEnd(tokens, lastNode.name, last.end);
    } catch {
      // Unreachable after a successful parse; skip bookending rather than guess.
      return null;
    }
    if (endTagEnd !== null) end = endTagEnd;
  }
  return { start: first.begin, end };
}

/**
 * Offset just past the `{% end<tagName> %}` closing the block tag whose opening
 * token ends at `searchFrom`, or `null` when no closer exists ahead (a
 * standalone tag). `{% raw %}` content never appears here (the tokenizer
 * collapses it into HTML tokens), but `{% comment %}` contents still tokenize
 * as tags, so the scan jumps from a `comment` token to its `endcomment`.
 */
export function findMatchingEndTagEnd(
  tokens: TopLevelToken[],
  tagName: string,
  searchFrom: number,
): number | null {
  const endTagName = `end${tagName}`;
  let depth = 1;
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    if (!token || !TypeGuards.isTagToken(token) || token.begin < searchFrom) {
      continue;
    }
    if (token.name === endTagName) {
      depth -= 1;
      if (depth === 0) return token.end;
    } else if (token.name === tagName) {
      // Comments don't nest — the first `endcomment` closes, so a literal
      // `{% comment %}` inside a comment body must not deepen the count.
      if (tagName !== "comment") depth += 1;
    } else if (token.name === "comment") {
      while (true) {
        i += 1;
        const inner = tokens[i];
        if (inner === undefined) {
          throw new Error(
            `Unterminated {% comment %} while scanning for {% ${endTagName} %}`,
          );
        }
        if (TypeGuards.isTagToken(inner) && inner.name === "endcomment") break;
      }
    }
  }
  return null;
}

function collectPropertyAccess(
  tok: PropertyAccessToken,
  positions: Map<string, number>,
): void {
  // A property access is `[root, ...segments]` in `props`, and `variable` is set
  // ONLY when the receiver is a literal — `{{ "abc".size }}`, `{{ (1..3).first }}`,
  // `{{ 5.foo }}`. A literal receiver references no column, so there is nothing
  // to collect. Everything reachable from profile data (a bare identifier, a
  // dotted path, a top-level bracket key) leaves `variable` undefined and puts
  // its root at `props[0]`.
  if (tok.variable !== undefined) return;
  const [root, ...segments] = tok.props;
  if (!root) return;

  if (TypeGuards.isQuotedToken(root)) {
    // Top-level bracket key `["dotted.key"]`: capture the flat key so control-flow
    // matches how substitution/rendering resolve it, else it never surfaces as a scenario.
    // `content` is the unquoted, escape-decoded key — exactly what the renderer
    // looks up.
    if (root.content) record(positions, root.content, root.begin);
    return;
  }
  // A computed root (`{{ [some_var] }}`) names no static column.
  if (!TypeGuards.isWordToken(root)) return;

  const rootName = root.content;
  if (EXCLUDED_ROOTS.has(rootName)) return;

  if (rootName === "row") {
    // Normalize row.X / row["X"] / row['X'] → bare X. Bare row and dynamic
    // bracket access (row[some_var]) yield no static column name.
    const first = segments[0];
    if (!first) return;
    if (TypeGuards.isWordToken(first)) {
      record(positions, first.content, first.begin);
    } else if (TypeGuards.isQuotedToken(first) && first.content) {
      record(positions, first.content, first.begin);
    }
    return;
  }

  const parts: string[] = [rootName];
  for (const p of segments) {
    if (!TypeGuards.isWordToken(p)) break;
    parts.push(p.content);
  }
  record(positions, parts.join("."), root.begin);
}
